<template>
    <div class="file-viewer-image tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-image">
        <file-viewer-controls class="file-viewer-toolbar tw:justify-end" data-test="file-viewer-image-controls">
            <button
                type="button"
                class="button is-small is-white"
                aria-label="Zoom out"
                data-test="file-viewer-image-zoom-out"
                :disabled="zoom <= minZoom"
                @click="zoomOut"
            >
                <i class="uil uil-minus" aria-hidden="true" />
            </button>
            <span
                class="tw:text-sm tw:tabular-nums"
                data-test="file-viewer-image-zoom"
                aria-live="polite"
            >
                {{ Math.round(zoom * 100) }}%
            </span>
            <button
                type="button"
                class="button is-small is-white"
                aria-label="Zoom in"
                data-test="file-viewer-image-zoom-in"
                :disabled="zoom >= maxZoom"
                @click="zoomIn"
            >
                <i class="uil uil-plus" aria-hidden="true" />
            </button>
            <button
                type="button"
                class="button is-small is-white"
                aria-label="Fit to width"
                data-test="file-viewer-image-fit-width"
                @click="fitToWidth"
            >
                <i class="uil uil-expand-arrows-alt" aria-hidden="true" />
            </button>
        </file-viewer-controls>

        <div
            ref="viewport"
            class="file-viewer-image__viewport tw:flex-1 tw:min-h-0 tw:overflow-auto tw:bg-gray-50 tw:rounded tw:cursor-grab"
            :class="{ 'tw:cursor-grabbing': isPanning }"
            data-test="file-viewer-image-viewport"
            tabindex="-1"
            @mousedown="onPanStart"
            @mousemove="onPanMove"
            @mouseup="onPanEnd"
            @mouseleave="onPanEnd"
            @blur="onPanEnd"
        >
            <div class="file-viewer-image__canvas tw:inline-block tw:origin-top-left" :style="canvasStyle">
                <img
                    ref="imageEl"
                    :src="src"
                    :alt="alt"
                    class="tw:max-w-none tw:select-none tw:pointer-events-none"
                    draggable="false"
                    data-test="file-viewer-image-img"
                    @load="onImageLoad"
                    @error="$emit('error')"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import FileViewerControls from '@/components/file-viewer/FileViewerControls.vue';

defineProps({
    src: {
        type: String,
        required: true,
    },
    alt: {
        type: String,
        default: 'Attachment preview',
    },
});

const emit = defineEmits(['ready', 'error']);

const minZoom = 0.25;
const maxZoom = 3;
const zoomStep = 0.25;
const viewportPadding = 16;

const zoom = ref(1);
const offsetX = ref(0);
const offsetY = ref(0);
const isPanning = ref(false);
const panStart = ref({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
const viewport = ref(null);
const imageEl = ref(null);

const canvasStyle = computed(() => ({
    transform: `translate(${offsetX.value}px, ${offsetY.value}px) scale(${zoom.value})`,
}));

function clampZoom(value) {
    return Math.min(maxZoom, Math.max(minZoom, Number(value.toFixed(2))));
}

function zoomIn() {
    zoom.value = clampZoom(zoom.value + zoomStep);
}

function zoomOut() {
    zoom.value = clampZoom(zoom.value - zoomStep);
}

/**
 * Scale the image so its natural width fits the viewport width.
 */
function fitToWidth() {
    const vp = viewport.value;
    const image = imageEl.value;
    const naturalWidth = image?.naturalWidth;

    if (!vp || !naturalWidth) {
        zoom.value = 1;
        offsetX.value = 0;
        offsetY.value = 0;
        return;
    }

    const availableWidth = Math.max(1, vp.clientWidth - viewportPadding);
    zoom.value = clampZoom(availableWidth / naturalWidth);
    offsetX.value = 0;
    offsetY.value = 0;
}

function onImageLoad() {
    fitToWidth();
    emit('ready');
}

function onPanStart(event) {
    if (event.button !== 0) {
        return;
    }

    isPanning.value = true;
    panStart.value = {
        x: event.clientX,
        y: event.clientY,
        offsetX: offsetX.value,
        offsetY: offsetY.value,
    };
}

function onPanMove(event) {
    if (!isPanning.value) {
        return;
    }

    offsetX.value = panStart.value.offsetX + (event.clientX - panStart.value.x);
    offsetY.value = panStart.value.offsetY + (event.clientY - panStart.value.y);
}

function onPanEnd() {
    isPanning.value = false;
}
</script>
